// src/services/petApi.js
// Servicio para manejar operaciones relacionadas con mascotas

import { dotoniwoofApi } from '../api/dotoniwooApi';

/**
 * Registra una nueva mascota
 * @param {Object} petData - Datos de la mascota
 * @param {number} petData.id_client - ID del cliente dueño
 * @param {string} petData.name - Nombre de la mascota
 * @param {number} petData.id_specie - ID de la especie
 * @param {string} petData.breed - Raza de la mascota
 * @param {number} petData.weight_kg - Peso en kilogramos
 * @param {string} petData.birthdate - Fecha de nacimiento (YYYY-MM-DD)
 * @param {string} petData.notes - Notas adicionales
 * @param {string} petData.profile_image_url - URL de la imagen de perfil (opcional)
 * @returns {Promise<Object>} Respuesta de la API
 */
export const createPet = async (petData) => {
  try {
    console.log('Enviando datos de la mascota:', petData);
    
    const response = await dotoniwoofApi.post('/pet.php', petData);
    
    console.log('Respuesta del servidor:', response.data);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al crear mascota:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Obtiene la lista de mascotas con paginación y búsqueda
 * @param {Object} params - Parámetros de consulta
 * @param {number} params.page - Número de página (por defecto 1)
 * @param {number} params.per_page - Elementos por página (1-100, por defecto 20)
 * @param {string} params.q - Término de búsqueda (opcional)
 * @returns {Promise<Object>} Lista de mascotas con metadatos de paginación
 */
export const getPets = async ({ page = 1, per_page = 20, q = '' } = {}) => {
  try {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('per_page', per_page.toString());
    if (q) params.append('q', q);
    
    const response = await dotoniwoofApi.get(`/pet.php?${params.toString()}`);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener mascotas:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Obtiene una mascota por su ID
 * @param {number} petId - ID de la mascota
 * @returns {Promise<Object>} Datos de la mascota
 */
export const getPetById = async (petId) => {
  try {
    const response = await dotoniwoofApi.get(`/pet.php/${petId}`);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener mascota:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Actualiza una mascota existente
 * @param {number} petId - ID de la mascota
 * @param {Object} petData - Nuevos datos de la mascota
 * @param {number} [petData.id_client] - ID del cliente dueño
 * @param {string} [petData.name] - Nombre de la mascota
 * @param {number} [petData.id_specie] - ID de la especie
 * @param {string} [petData.breed] - Raza de la mascota
 * @param {number} [petData.weight_kg] - Peso en kilogramos
 * @param {string} [petData.birthdate] - Fecha de nacimiento (YYYY-MM-DD)
 * @param {string} [petData.notes] - Notas adicionales
 * @param {string} [petData.profile_image_url] - URL de la imagen de perfil
 * @returns {Promise<Object>} Respuesta de la API
 */
export const updatePet = async (petId, petData) => {
  try {
    console.log('Actualizando mascota:', petId, petData);
    
    const response = await dotoniwoofApi.put(`/pet.php/${petId}`, petData);
    
    console.log('Respuesta del servidor:', response.data);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al actualizar mascota:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Elimina una mascota
 * @param {number} petId - ID de la mascota
 * @returns {Promise<Object>} Respuesta de la API
 */
export const deletePet = async (petId) => {
  try {
    const response = await dotoniwoofApi.delete(`/pet.php/${petId}`);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al eliminar mascota:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Obtiene las mascotas de un cliente por su ID
 * @param {number} clientId - ID del cliente
 * @returns {Promise<Object>} Lista de mascotas del cliente
 */
export const getPetsByClientId = async (clientId) => {
  try {
    const response = await dotoniwoofApi.get(`/pet.php/by-client/${clientId}`);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener mascotas por cliente:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Obtiene las especies disponibles
 * @returns {Promise<Object>} Lista de especies
 */
export const getSpecies = async () => {
  try {
    const response = await dotoniwoofApi.get('/species.php');
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener especies:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};
